import {
  type DefaultError,
  type QueryClient,
  type QueryKey,
  type UseMutationOptions,
  useMutation,
} from '@tanstack/react-query';

// biome-ignore lint/suspicious/noExplicitAny: Accept any function
export type Variables<Func extends (...args: any) => any> = Parameters<Func>[0];

type OptimisticMutationContext<TQueryData> = { previousValue: TQueryData | undefined };

// Options callers of an optimistic mutation hook may pass through - the mutationFn and
// optimistic onMutate are owned by the hook itself, so they're excluded here.
export type OptimisticMutationOptions<TQueryData, TVariables, TError = DefaultError> = Omit<
  UseMutationOptions<void, TError, TVariables, OptimisticMutationContext<TQueryData>>,
  'mutationFn' | 'onMutate'
>;

export function useOptimisticMutation<TQueryData, TVariables, TError = DefaultError>({
  queryClient,
  queryKey,
  mutationFn,
  optimisticUpdate,
  invalidateKeys,
  ...options
}: {
  queryClient: QueryClient;
  queryKey: QueryKey;
  mutationFn: (data: TVariables) => Promise<void>;
  optimisticUpdate: (prev: TQueryData | undefined, data: TVariables) => TQueryData | undefined;
  // Extra keys to invalidate on settle, beyond the optimistically-updated `queryKey`.
  invalidateKeys?: QueryKey[];
} & OptimisticMutationOptions<TQueryData, TVariables, TError>) {
  // return useMutation<void, TError, TVariables, { previousValue: TQueryData | undefined }>({
  return useMutation<void, TError, TVariables, OptimisticMutationContext<TQueryData>>({
    ...options,
    mutationFn: mutationFn,
    onMutate: async (data) => {
      // Cancel any queries currently going on (so they don't overwrite optimistic value)
      await queryClient.cancelQueries({ queryKey });

      const previousValue = queryClient.getQueryData<TQueryData>(queryKey);

      queryClient.setQueryData<TQueryData>(queryKey, (prev) => optimisticUpdate(prev, data));

      // Return previous value to fall back to if mutation fails
      return { previousValue };
    },
    onError: (err, variables, onMutateResult, context) => {
      // Fall back to previous value (before mutation failed)
      if (onMutateResult?.previousValue) {
        queryClient.setQueryData(queryKey, onMutateResult.previousValue);
      }
      options.onError?.(err, variables, onMutateResult, context);
    },
    onSettled: (data, error, variables, onMutateResult, context) => {
      // Invalidate the optimistically-updated query plus any related keys
      void queryClient.invalidateQueries({ queryKey });
      for (const key of invalidateKeys ?? []) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      options.onSettled?.(data, error, variables, onMutateResult, context);
    },
  });
}
